import { useCallback, useEffect, useRef, useState } from 'react';
import { agentFields, agentUpdate, effectiveTalkMode, focusKey, isTalkMode, micGate } from './voiceTalk.js';

/**
 * useVoiceSession — browser client for mhi-voice (MHI Assistant voice).
 *
 * Protocol (mhi-voice/mhi_voice/protocol.py):
 *   out: session.start {asset, asset_label, focus_part_id, focus_part_name, telemetry, sample_rate:16000,
 *        mode?, client_id?, talk_mode?, ui_focus?} · binary PCM16 LE 16 kHz mono, 20 ms frames ·
 *        context.update · input.text · session.end · ping
 *   in:  session.ready · stt.partial · stt.final · turn.thinking · bot.audio.start + binary PCM16 at
 *        the announced rate · bot.text · bot.audio.end · bot.interrupted · ui.action · citations ·
 *        metrics · error · pong · ui.mode · agent.state
 *
 * Agent mode (CONTRACT 9.3): `mode` ('assistant' | 'agent'), `clientId` (the same id the agent socket
 * uses), `talkMode` ('open' | 'wake' | 'ptt') and `uiFocus` (the FocusState on screen) travel in
 * session.start and, when they change, in context.update. `ui.mode` ("open agent mode") calls
 * onUiMode(agent); `agent.state` (narration, talk mode, dictation, voice confirmation) calls
 * onAgentState(state). Voice never sends an `asset` ui.action, so UI_ACTION_TYPES stays as it was.
 * talk_mode is sent only when the page's value differs from what the server last reported, so a voice
 * switch ("MHI, push to talk") is not overwritten by the next focus update. In push to talk (`ptt`)
 * the mic is gated here: frames go out only while setPttHeld(true) (the hold-to-talk button or the
 * space bar), then silence for PTT_TRAIL_MS so the server closes the turn (voiceTalk.js).
 *
 * The mic stays open while the assistant speaks (barge-in); the browser's echo
 * cancellation keeps the assistant out of the mic. Call start() from a click
 * handler, or pass an AudioContext created in one (GUIDELINES 14).
 *
 * States: idle | connecting | listening | user_speaking | thinking | speaking | error
 */

const TARGET_RATE = 16000;
const FRAME_MS = 20;
const PING_MS = 20000;
const TELEMETRY_MIN_MS = 5000;
const MAX_RECONNECTS = 5;
const LOCAL_BARGE_RMS = 0.05;      // mic level that counts as the user talking over playback
const LOCAL_BARGE_MS = 300;
const LOCAL_BARGE_AFTER_MS = 400;  // ignore the first moments of playback (echo tail)
const UI_ACTION_TYPES = new Set(['select', 'highlight', 'explode', 'open_workspace', 'trace', 'reset', 'back', 'deeper']);
// Capture worklet: downmix, resample to 16 kHz (linear), emit 20 ms Int16 frames + RMS.
const WORKLET_SOURCE = `
class MhiCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = (options && options.processorOptions) || {};
    this.target = o.targetRate || ${TARGET_RATE};
    this.frame = Math.round(this.target * (o.frameMs || ${FRAME_MS}) / 1000);
    this.ratio = sampleRate / this.target;
    this.pos = 0;
    this.prev = 0;
    this.out = new Int16Array(this.frame);
    this.n = 0;
    this.sum = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const ch = input[0];
    for (let i = 0; i < ch.length; i++) {
      const cur = ch[i];
      if (this.ratio === 1) { this.push(cur); continue; }
      while (this.pos <= 1) {
        this.push(this.prev + (cur - this.prev) * this.pos);
        this.pos += this.ratio;
      }
      this.pos -= 1;
      this.prev = cur;
    }
    return true;
  }
  push(v) {
    const s = v < -1 ? -1 : v > 1 ? 1 : v;
    this.out[this.n++] = s < 0 ? s * 0x8000 : s * 0x7fff;
    this.sum += s * s;
    if (this.n === this.frame) {
      const buf = this.out.buffer;
      this.port.postMessage({ pcm: buf, rms: Math.sqrt(this.sum / this.frame) }, [buf]);
      this.out = new Int16Array(this.frame);
      this.n = 0;
      this.sum = 0;
    }
  }
}
registerProcessor('mhi-capture', MhiCaptureProcessor);
`;

/** Main-thread capture for browsers where AudioWorklet is missing or never loads. */
function createScriptProcessorCapture(ctx, onFrame) {
  const node = ctx.createScriptProcessor(1024, 1, 1);
  const frame = Math.round((TARGET_RATE * FRAME_MS) / 1000);
  const ratio = ctx.sampleRate / TARGET_RATE;
  let pos = 0;
  let prev = 0;
  let out = new Int16Array(frame);
  let n = 0;
  let sum = 0;
  const push = (v) => {
    const s = v < -1 ? -1 : v > 1 ? 1 : v;
    out[n++] = s < 0 ? s * 0x8000 : s * 0x7fff;
    sum += s * s;
    if (n === frame) {
      onFrame({ data: { pcm: out.buffer, rms: Math.sqrt(sum / frame) } });
      out = new Int16Array(frame);
      n = 0;
      sum = 0;
    }
  };
  node.onaudioprocess = (e) => {
    const ch = e.inputBuffer.getChannelData(0);
    for (let i = 0; i < ch.length; i++) {
      const cur = ch[i];
      if (ratio === 1) { push(cur); continue; }
      while (pos <= 1) {
        push(prev + (cur - prev) * pos);
        pos += ratio;
      }
      pos -= 1;
      prev = cur;
    }
  };
  return node;
}

const WORKLET_LOAD_TIMEOUT_MS = 3000;

async function createWorkletCapture(ctx, onFrame) {
  if (!ctx.audioWorklet || typeof AudioWorkletNode === 'undefined') throw new Error('AudioWorklet unsupported');
  const blobUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
  let timer;
  try {
    await Promise.race([
      ctx.audioWorklet.addModule(blobUrl),
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('AudioWorklet load timed out')), WORKLET_LOAD_TIMEOUT_MS); }),
    ]);
  } finally {
    clearTimeout(timer);
    URL.revokeObjectURL(blobUrl);
  }
  const node = new AudioWorkletNode(ctx, 'mhi-capture', {
    numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1,
    processorOptions: { targetRate: TARGET_RATE, frameMs: FRAME_MS },
  });
  node.port.onmessage = onFrame;
  return node;
}

export default function useVoiceSession({
  url,
  asset,
  assetLabel,
  focusPartId = null,
  focusPartName = null,
  telemetry = null,
  onUiAction,
  /** 'assistant' (default) | 'agent'. */
  mode = null,
  clientId = null,
  /** 'open' | 'wake' | 'ptt'; the voice service defaults to 'wake' in agent mode. */
  talkMode = null,
  /** FocusState of what is on screen (agent mode). */
  uiFocus = null,
  /** ui.mode from voice ("open agent mode" / "close agent mode"): onUiMode(agent: boolean). */
  onUiMode,
  /** agent.state from voice: onAgentState({mode, narration, talk_mode, dictation, confirm}). */
  onAgentState,
} = {}) {
  const [state, setState] = useState('idle');
  const [partial, setPartial] = useState('');
  const [userText, setUserText] = useState('');
  const [answer, setAnswer] = useState('');
  const [evidence, setEvidence] = useState({ items: [], parts: [] });
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [muted, setMutedState] = useState(false);
  const [providers, setProviders] = useState(null);
  const [agentState, setAgentState] = useState(null);
  // 'running' | 'suspended' | 'closed' | null: a suspended AudioContext needs one user gesture.
  const [audioState, setAudioState] = useState(null);

  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const ownsCtxRef = useRef(false);
  const streamRef = useRef(null);
  const nodesRef = useRef([]);
  const readyRef = useRef(false);
  const activeRef = useRef(false);        // a session the user has started and not ended
  const reconnectsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const telemetryTimerRef = useRef(null);
  const mutedRef = useRef(false);
  // Push to talk: the gating talk mode (null outside agent mode), held flag and release time.
  const talkGateRef = useRef(null);
  const pttHeldRef = useRef(false);
  const pttReleasedAtRef = useRef(0);
  const [pttHeld, setPttHeldState] = useState(false);
  const stateRef = useRef('idle');

  // playback
  const playTurnRef = useRef(null);
  const playRateRef = useRef(24000);
  const nextTimeRef = useRef(0);
  const sourcesRef = useRef(new Set());
  const audioEndedRef = useRef(false);
  const playStartedAtRef = useRef(0);
  const loudSinceRef = useRef(0);

  // latest context, read by long-lived callbacks
  const ctxInfoRef = useRef({});
  ctxInfoRef.current = { asset, assetLabel, focusPartId, focusPartName, telemetry, mode, clientId, talkMode, uiFocus };
  const onUiActionRef = useRef(onUiAction);
  onUiActionRef.current = onUiAction;
  const onUiModeRef = useRef(onUiMode);
  onUiModeRef.current = onUiMode;
  const onAgentStateRef = useRef(onAgentState);
  onAgentStateRef.current = onAgentState;
  const lastSentRef = useRef({ asset: undefined, focus: undefined, telemetry: '', telemetryAt: 0 });
  // Agent-mode fields last sent (session.start or context.update).
  const lastAgentRef = useRef({ mode: undefined, talkMode: undefined, focus: '' });

  const setStateBoth = useCallback((s) => {
    stateRef.current = s;
    setState(s);
  }, []);

  const sendJson = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  /* ---- playback ------------------------------------------------------- */
  const stopPlayback = useCallback(() => {
    sourcesRef.current.forEach((src) => {
      try { src.stop(); } catch { /* already stopped */ }
    });
    sourcesRef.current.clear();
    playTurnRef.current = null;
    nextTimeRef.current = 0;
    audioEndedRef.current = false;
  }, []);

  const maybeFinishSpeaking = useCallback(() => {
    if (audioEndedRef.current && sourcesRef.current.size === 0 && stateRef.current === 'speaking') {
      audioEndedRef.current = false;
      setStateBoth('listening');
    }
  }, [setStateBoth]);

  const playPcm = useCallback((arrayBuffer) => {
    const ctx = ctxRef.current;
    if (!ctx || !playTurnRef.current || arrayBuffer.byteLength < 2) return;
    const pcm = new Int16Array(arrayBuffer, 0, Math.floor(arrayBuffer.byteLength / 2));
    const buffer = ctx.createBuffer(1, pcm.length, playRateRef.current);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) data[i] = pcm[i] / 32768;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime + 0.03, nextTimeRef.current);
    if (sourcesRef.current.size === 0 && nextTimeRef.current < ctx.currentTime) {
      playStartedAtRef.current = performance.now();
    }
    src.start(startAt);
    nextTimeRef.current = startAt + buffer.duration;
    sourcesRef.current.add(src);
    src.onended = () => {
      sourcesRef.current.delete(src);
      maybeFinishSpeaking();
    };
  }, [maybeFinishSpeaking]);

  /* ---- inbound -------------------------------------------------------- */
  const handleMessage = useCallback((msg) => {
    if (!msg || typeof msg.type !== 'string') return;
    switch (msg.type) {
      case 'session.ready':
        readyRef.current = true;
        reconnectsRef.current = 0;
        setProviders(msg.providers || null);
        setError(null);
        setStateBoth('listening');
        break;
      case 'stt.partial':
        if (msg.vad === 'start') setStateBoth('user_speaking');
        else if (msg.vad === 'end') { if (stateRef.current === 'user_speaking') setStateBoth('thinking'); }
        else if (typeof msg.text === 'string') { setPartial(msg.text); setStateBoth('user_speaking'); }
        break;
      case 'stt.final':
        if (typeof msg.text === 'string') {
          setUserText(msg.text);
          setPartial('');
          setAnswer('');
          setEvidence({ items: [], parts: [] });
          setStateBoth('thinking');
        }
        break;
      case 'turn.thinking':
        setStateBoth('thinking');
        break;
      case 'bot.audio.start':
        stopPlayback();
        playTurnRef.current = msg.turn_id;
        playRateRef.current = Number(msg.sample_rate) > 0 ? Number(msg.sample_rate) : 24000;
        playStartedAtRef.current = performance.now();
        if (ctxRef.current?.state === 'suspended') ctxRef.current.resume().catch(() => {});
        setStateBoth('speaking');
        break;
      case 'bot.text':
        if (typeof msg.text !== 'string') break;
        if (msg.final) setAnswer(msg.text);
        else setAnswer((prev) => (prev ? `${prev} ${msg.text}` : msg.text));
        break;
      case 'bot.audio.end':
        if (msg.turn_id === playTurnRef.current) {
          audioEndedRef.current = true;
          maybeFinishSpeaking();
        }
        break;
      case 'bot.interrupted':
        stopPlayback();
        if (stateRef.current === 'speaking' || stateRef.current === 'thinking') setStateBoth('user_speaking');
        break;
      case 'ui.action': {
        const action = msg.action;
        if (!action || !UI_ACTION_TYPES.has(action.type)) break;
        if (action.part_ids && !Array.isArray(action.part_ids)) break;
        onUiActionRef.current?.(action);
        break;
      }
      case 'citations':
        setEvidence({
          items: Array.isArray(msg.items) ? msg.items : [],
          parts: Array.isArray(msg.parts) ? msg.parts : [],
        });
        break;
      case 'metrics':
        setMetrics(msg);
        break;
      case 'error':
        console.warn('[Voice] server error', msg.code);
        setError(typeof msg.message === 'string' ? msg.message : 'Voice assistant error');
        if (msg.code === 'busy' || msg.code === 'idle_timeout') {
          activeRef.current = false;
          setStateBoth('error');
        }
        break;
      case 'ui.mode':
        if (typeof msg.agent === 'boolean') onUiModeRef.current?.(msg.agent);
        break;
      case 'agent.state': {
        const next = {
          mode: msg.mode ?? null,
          narration: msg.narration ?? null,
          talk_mode: msg.talk_mode ?? null,
          dictation: Boolean(msg.dictation),
          confirm: msg.confirm && typeof msg.confirm === 'object' ? msg.confirm : null,
        };
        // What the server now holds: the page's talkMode only needs sending when it differs.
        if (next.mode === 'agent' && isTalkMode(next.talk_mode)) {
          lastAgentRef.current = { ...lastAgentRef.current, talkMode: next.talk_mode };
        }
        setAgentState(next);
        onAgentStateRef.current?.(next);
        break;
      }
      case 'pong':
        break;
      default:
        break;
    }
  }, [maybeFinishSpeaking, setStateBoth, stopPlayback]);

  /* ---- mic ------------------------------------------------------------ */
  const onMicFrame = useCallback((event) => {
    const { pcm, rms } = event.data || {};
    if (!pcm) return;
    const gate = micGate({
      muted: mutedRef.current, talkMode: talkGateRef.current, held: pttHeldRef.current,
      releasedAt: pttReleasedAtRef.current, now: performance.now(),
    });
    // Local barge-in: stop playback at once when the user clearly talks over it.
    if (stateRef.current === 'speaking' && gate === 'send') {
      const now = performance.now();
      if (rms > LOCAL_BARGE_RMS && now - playStartedAtRef.current > LOCAL_BARGE_AFTER_MS) {
        if (!loudSinceRef.current) loudSinceRef.current = now;
        if (now - loudSinceRef.current >= LOCAL_BARGE_MS) {
          console.log('[Voice] local barge-in');
          stopPlayback();
          loudSinceRef.current = 0;
        }
      } else {
        loudSinceRef.current = 0;
      }
    }
    const ws = wsRef.current;
    if (gate === 'drop' || !readyRef.current || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(gate === 'silence' ? new ArrayBuffer(pcm.byteLength) : pcm);
  }, [stopPlayback]);

  const releaseAudio = useCallback(() => {
    nodesRef.current.forEach((n) => {
      try { n.disconnect(); } catch { /* not connected */ }
      if (n.port) n.port.onmessage = null;
      if ('onaudioprocess' in n) n.onaudioprocess = null;
    });
    nodesRef.current = [];
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    stopPlayback();
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx) ctx.onstatechange = null;
    if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {});
    ownsCtxRef.current = false;
    setAudioState(null);
  }, [stopPlayback]);

  /* ---- socket --------------------------------------------------------- */
  const clearTimers = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    clearInterval(pingTimerRef.current);
    clearInterval(telemetryTimerRef.current);
  }, []);

  const connect = useCallback(() => {
    if (!url) {
      setError('Voice endpoint is not configured');
      setStateBoth('error');
      return;
    }
    readyRef.current = false;
    setStateBoth('connecting');
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.error('[Voice] socket failed', e);
      setError('Could not open the voice connection');
      setStateBoth('error');
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      const c = ctxInfoRef.current;
      const telemetryJson = c.telemetry ? JSON.stringify(c.telemetry) : '';
      ws.send(JSON.stringify({
        type: 'session.start',
        asset: c.asset,
        asset_label: c.assetLabel || undefined,
        focus_part_id: c.focusPartId || null,
        focus_part_name: c.focusPartName || null,
        telemetry: c.telemetry || null,
        sample_rate: TARGET_RATE,
        ...agentFields(c),
      }));
      lastSentRef.current = { asset: c.asset, focus: c.focusPartId || null, telemetry: telemetryJson, telemetryAt: Date.now() };
      lastAgentRef.current = { mode: c.mode, talkMode: c.talkMode, focus: focusKey(c.uiFocus) };
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = setInterval(() => sendJson({ type: 'ping' }), PING_MS);
    };
    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        handleMessage(msg);
      } else if (event.data instanceof ArrayBuffer) {
        playPcm(event.data);
      }
    };
    ws.onerror = () => console.warn('[Voice] socket error');
    ws.onclose = () => {
      clearInterval(pingTimerRef.current);
      if (wsRef.current === ws) wsRef.current = null;
      readyRef.current = false;
      stopPlayback();
      if (!activeRef.current) return;
      if (reconnectsRef.current >= MAX_RECONNECTS) {
        activeRef.current = false;
        setError('Voice connection lost');
        setStateBoth('error');
        return;
      }
      const delay = Math.min(8000, 1000 * 2 ** reconnectsRef.current);
      reconnectsRef.current += 1;
      console.log(`[Voice] reconnecting in ${delay} ms (attempt ${reconnectsRef.current})`);
      setStateBoth('connecting');
      reconnectTimerRef.current = setTimeout(() => { if (activeRef.current) connect(); }, delay);
    };
  }, [url, handleMessage, playPcm, sendJson, setStateBoth, stopPlayback]);

  /**
   * Start a session. `prearmedCtx`: an AudioContext created inside the click
   * handler that opened the voice UI (resumed there), so playback and capture run.
   */
  const start = useCallback(async (prearmedCtx = null) => {
    if (activeRef.current) return;
    activeRef.current = true;
    reconnectsRef.current = 0;
    setError(null);
    setPartial('');
    setUserText('');
    setAnswer('');
    setEvidence({ items: [], parts: [] });
    setMetrics(null);
    setStateBoth('connecting');
    try {
      let ctx = prearmedCtx && prearmedCtx.state !== 'closed' ? prearmedCtx : null;
      if (!ctx) {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
      }
      ctxRef.current = ctx;
      ownsCtxRef.current = true;
      setAudioState(ctx.state);
      ctx.onstatechange = () => { if (ctxRef.current === ctx) setAudioState(ctx.state); };
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      console.log('[Voice] start: ctx', ctx.state, ctx.sampleRate, 'Hz; requesting microphone');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (!activeRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      console.log('[Voice] microphone granted; loading capture worklet');

      let node;
      try {
        node = await createWorkletCapture(ctx, onMicFrame);
      } catch (e) {
        console.warn(`[Voice] ${e.message}; using ScriptProcessor capture`);
        node = createScriptProcessorCapture(ctx, onMicFrame);
      }
      if (!activeRef.current) return;
      const source = ctx.createMediaStreamSource(stream);
      // Keep the graph pulling samples on every platform: terminate at a muted gain.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(node);
      node.connect(sink);
      sink.connect(ctx.destination);
      nodesRef.current = [source, node, sink];
      console.log('[Voice] mic ready: ctx', ctx.state, ctx.sampleRate, 'Hz');
      connect();
    } catch (e) {
      console.error('[Voice] start failed', e);
      activeRef.current = false;
      releaseAudio();
      setError(e?.name === 'NotAllowedError' ? 'Microphone access was denied' : 'Could not start the microphone');
      setStateBoth('error');
    }
  }, [connect, onMicFrame, releaseAudio, setStateBoth]);

  const end = useCallback(() => {
    activeRef.current = false;
    clearTimers();
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'session.end' }));
        ws.close(1000);
      } catch { /* closing anyway */ }
    }
    readyRef.current = false;
    releaseAudio();
    setPartial('');
    setStateBoth('idle');
  }, [clearTimers, releaseAudio, setStateBoth]);

  /** Push to talk: hold (true) opens the mic, release (false) sends the trailing silence. */
  const setPttHeld = useCallback((next) => {
    const held = Boolean(next);
    if (held === pttHeldRef.current) return;
    pttHeldRef.current = held;
    if (!held) pttReleasedAtRef.current = performance.now();
    setPttHeldState(held);
  }, []);

  const setMuted = useCallback((next) => {
    mutedRef.current = next;
    setMutedState(next);
    streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
  }, []);

  /** Resume a suspended AudioContext; call it from a click (the one gesture an auto-started session needs). */
  const resumeAudio = useCallback(() => {
    const ctx = ctxRef.current;
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  }, []);

  const sendText = useCallback((text) => {
    const t = String(text || '').trim();
    if (!t || !readyRef.current) return false;
    stopPlayback();
    sendJson({ type: 'input.text', text: t.slice(0, 1000) });
    return true;
  }, [sendJson, stopPlayback]);

  /* ---- context updates -------------------------------------------------- */
  useEffect(() => {
    if (!readyRef.current) return;
    const last = lastSentRef.current;
    const focus = focusPartId || null;
    if (asset === last.asset && focus === last.focus) return;
    const msg = { type: 'context.update', focus_part_id: focus, focus_part_name: focusPartName || null };
    if (asset !== last.asset) {
      msg.asset = asset;
      msg.asset_label = assetLabel || null;
    }
    // The readings follow the new selection (or asset) at once, not on the next 5 s tick.
    const t = ctxInfoRef.current.telemetry;
    msg.telemetry = t || null;
    sendJson(msg);
    lastSentRef.current = { ...last, asset, focus, telemetry: t ? JSON.stringify(t) : '', telemetryAt: Date.now() };
  }, [asset, assetLabel, focusPartId, focusPartName, sendJson, state]);

  // Agent mode, talk mode and the on-screen focus follow the page (9.3), sent only when they change;
  // talk_mode only when it differs from the server's (agentUpdate).
  const uiFocusKey = focusKey(uiFocus);
  useEffect(() => {
    if (!readyRef.current) return;
    const update = agentUpdate(lastAgentRef.current, { mode, clientId, talkMode, uiFocus: ctxInfoRef.current.uiFocus });
    if (!update) return;
    sendJson({ type: 'context.update', ...update.fields });
    lastAgentRef.current = update.last;
  }, [mode, talkMode, clientId, uiFocusKey, sendJson, state]);

  // Push to talk gates the mic in agent mode; leaving ptt releases a held button.
  const talkGate = effectiveTalkMode({ mode, talkMode, agentState });
  const pttActive = talkGate === 'ptt';
  useEffect(() => {
    talkGateRef.current = talkGate;
    if (!pttActive && pttHeldRef.current) {
      pttHeldRef.current = false;
      setPttHeldState(false);
    }
  }, [talkGate, pttActive]);

  // Telemetry changes every tick; send at most once per TELEMETRY_MIN_MS.
  useEffect(() => {
    if (state === 'idle' || state === 'error') return undefined;
    telemetryTimerRef.current = setInterval(() => {
      if (!readyRef.current) return;
      const t = ctxInfoRef.current.telemetry;
      const json = t ? JSON.stringify(t) : '';
      const last = lastSentRef.current;
      if (json === last.telemetry || Date.now() - last.telemetryAt < TELEMETRY_MIN_MS) return;
      sendJson({ type: 'context.update', telemetry: t || null });
      lastSentRef.current = { ...last, telemetry: json, telemetryAt: Date.now() };
    }, 1000);
    return () => clearInterval(telemetryTimerRef.current);
  }, [state, sendJson]);

  // Release mic, AudioContext and socket on unmount.
  useEffect(() => () => {
    activeRef.current = false;
    clearTimers();
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) { try { ws.close(1000); } catch { /* closing */ } }
    releaseAudio();
  }, [clearTimers, releaseAudio]);

  return {
    state, partial, userText, answer, evidence, metrics, error, muted, providers, agentState, audioState,
    pttActive, pttHeld, start, end, setMuted, setPttHeld, sendText, resumeAudio,
  };
}
